import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import type { PersonProfile } from '@shared/views';
import type { ServicePeriod } from '@shared/types';
import { api } from '../api/client.js';
import { downloadProfilePdf } from '../lib/profilePdf.js';
import { navigate } from '../router.js';

/**
 * การ์ดประวัติรายบุคคล
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมหน้านี้ถูกจัดใหม่
 * ────────────────────────────────────────────────────────────────
 *
 * แบบเดิมวางทุกเรื่องเป็นการ์ดขนาดเท่ากันในตารางเดียว แล้วการ์ดที่กางออกจะกินเต็มแถว
 * ผลคือกดกางสองใบ หน้าจอเหลือช่องว่างครึ่งจอ และการ์ดที่เหลือกระโดดไปมาทุกครั้งที่กด
 *
 * ที่แย่กว่านั้นคือมันบอกลำดับความสำคัญผิด — "ประวัติการดำรงตำแหน่ง" ซึ่งเป็น
 * ข้อมูลจริงจาก DPIS และเป็นสิ่งเดียวในหน้านี้ที่ใช้อ้างอิงได้ ถูกวางเป็นการ์ดใบเล็ก
 * ขนาดเท่ากับ "รางวัล" ที่เป็นข้อมูลสมมุติและว่างเปล่า
 *
 * แบบใหม่แบ่งสองคอลัมน์ตามความน่าเชื่อถือของข้อมูล
 *
 *   คอลัมน์หลัก   ประวัติการดำรงตำแหน่ง — ของจริง กางอยู่ตลอด ไม่ต้องกด
 *   คอลัมน์ข้าง   เรื่องที่เป็นข้อมูลสมมุติ — ยุบไว้ใต้ป้ายเตือนใบเดียว
 *
 * ป้าย "ข้อมูลสมมุติ" จึงเหลือใบเดียวคุมทั้งคอลัมน์ แทนที่จะซ้ำแปดใบจนคนเลิกอ่าน
 * แต่ยังอยู่ในสายตาตลอดเวลาที่อ่านเรื่องพวกนั้น
 */
/**
 * การ์ดประวัติรายบุคคล
 *
 * embedded = ถูกวางอยู่ในกล่องอื่น เช่นหน้าต่างรายละเอียดที่เปิดจากผัง
 *
 * ⚠ ต่างกันแค่ "ปุ่มที่พาออกไปที่อื่น" เท่านั้น เนื้อหาเหมือนกันทุกส่วน
 * เพราะระบบเหลือหน้าเดียวแล้ว ปุ่มที่เปลี่ยนเส้นทางไปหน้าอื่นจะกดแล้วไม่มีอะไรเกิดขึ้น
 * ปุ่มที่กดแล้วเงียบแย่กว่าไม่มีปุ่ม — ตอน embedded จึงแสดงเส้นทางหน่วยงานเป็นข้อความเฉย ๆ
 *
 * ห้าม fork ไฟล์นี้ไปทำอีกเวอร์ชันสำหรับหน้าต่างรายละเอียด
 * ประวัติรายบุคคลมีสิบส่วนย่อยและมีกฎการแสดงผลของตัวเองเยอะมาก
 * (ที่มาข้อมูลรายส่วน · การเติมคำว่า "ระดับ" · การพิมพ์เฉพาะส่วนประวัติ)
 * สองชุดจะเพี้ยนจากกันภายในเดือนเดียวโดยไม่มีใครรู้ว่าชุดไหนถูก
 */
export function PersonCard({
  personaId,
  embedded = false,
  hideIdentity = false,
  hideCareer = false,
  hideActions = false,
}: {
  personaId: string;
  embedded?: boolean;
  /**
   * ตัดชื่อ-สกุล เส้นทางหน่วยงาน และตารางข้อมูลประจำตัวออก
   *
   * ⚠ ใช้เมื่อกล่องที่ครอบอยู่แสดงข้อมูลชุดนั้นไปแล้ว (เช่นแผงรายละเอียดอัตรา
   *   ที่มีตาราง "ผู้ครองปัจจุบัน" อยู่ด้านบน) การแสดงซ้ำไม่ได้แค่เปลืองที่ —
   *   ผู้อ่านจะเริ่มเทียบสองตารางหาว่าต่างกันตรงไหน ทั้งที่มันคือชุดเดียวกัน
   *
   * ⚠ ตัดเฉพาะส่วนที่ซ้ำ — ปุ่มบันทึก PDF กับพิมพ์มี hideActions คุมแยกต่างหาก
   */
  hideIdentity?: boolean;
  /**
   * ตัดคอลัมน์ "ประวัติการดำรงตำแหน่ง" ออก เหลือแต่ข้อมูลประกอบ
   *
   * ⚠ ใช้คู่กับ IncumbentCard ซึ่งวาดประวัติเป็นแกนเวลาของตัวเองแล้ว
   *   ถ้าไม่ตัด ผู้ใช้จะเห็นประวัติชุดเดียวกันสองแบบในจอเดียว แล้วต้องเดาว่าอันไหนถูก
   * ⚠ ไม่ได้ลบไทม์ไลน์ทิ้ง — หน้าประวัติรายบุคคลเต็ม (ไม่ embedded) ยังใช้ของเดิมอยู่
   */
  hideCareer?: boolean;
  /**
   * ตัดแถบปุ่ม "บันทึกเป็น PDF" กับ "พิมพ์" ออก
   *
   * ⚠ ใช้เมื่อกล่องที่ครอบอยู่มีปุ่มส่งออกของตัวเองแล้ว (แผงรายละเอียดอัตรา
   *   มีปุ่มส่งออกทั้งอัตราอยู่ที่หัวแผง ซึ่งได้เอกสารที่ครบกว่าประวัติรายบุคคลเปล่า ๆ)
   *   สองแถบปุ่มในจอเดียวที่ได้ไฟล์คนละแบบ บังคับให้ผู้ใช้เดาว่าอันไหนให้อะไร
   *
   * ⚠ ห้ามตั้งค่าเริ่มต้นเป็น true — หน้าประวัติรายบุคคลเต็มไม่มีที่อื่นให้กดเลย
   */
  hideActions?: boolean;
}) {
  const q = useQuery({ queryKey: ['profile', personaId], queryFn: () => api.profile(personaId) });

  if (q.isPending) return <p className="state">กำลังโหลดประวัติ…</p>;
  if (q.error) {
    return (
      <section>
        <div className="wrap">
          <p className="state error">{(q.error as Error).message}</p>
          {!embedded && (
            <p className="state sm">
              <button type="button" className="lbtn" onClick={() => navigate({ name: 'org' })}>
                กลับไปผังหน่วยงาน
              </button>
            </p>
          )}
        </div>
      </section>
    );
  }

  return (
    <Profile
      p={q.data}
      embedded={embedded}
      hideIdentity={hideIdentity}
      hideCareer={hideCareer}
      hideActions={hideActions}
    />
  );
}

function Profile({
  p,
  embedded = false,
  hideIdentity = false,
  hideCareer = false,
  hideActions = false,
}: {
  p: PersonProfile;
  embedded?: boolean;
  hideIdentity?: boolean;
  hideCareer?: boolean;
  hideActions?: boolean;
}) {
  /**
   * พิมพ์เฉพาะส่วนประวัติ ไม่เอาเมนูซ้ายกับปุ่มไปด้วย
   * ใช้กลไกเดียวกับหน้าเส้นทางก้าวหน้า (ดู .printing-profile ใน styles.css)
   */
  useEffect(() => {
    const clear = () => document.body.classList.remove('printing-profile');
    window.addEventListener('afterprint', clear);
    return () => {
      clear();
      window.removeEventListener('afterprint', clear);
    };
  }, []);

  function print() {
    document.body.classList.add('printing-profile');
    // รอหนึ่งเฟรมให้เบราว์เซอร์จัด layout ตามสไตล์การพิมพ์ก่อนเปิดกล่องพิมพ์
    requestAnimationFrame(() => window.print());
  }

  /**
   * บันทึกเป็นไฟล์ PDF
   *
   * ตัวสร้าง PDF กับฟอนต์ภาษาไทยรวมกันหนักกว่า 400 KB จึงโหลดตอนกดปุ่มเท่านั้น
   * ครั้งแรกจึงใช้เวลาสักครู่ ต้องมีสถานะบอกผู้ใช้ ไม่ใช่ปุ่มที่กดแล้วเงียบไปเฉย ๆ
   * แล้วผู้ใช้กดซ้ำจนได้ไฟล์ซ้ำหลายใบ
   */
  const [saving, setSaving] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  async function savePdf() {
    setSaving(true);
    setPdfError(null);
    try {
      await downloadProfilePdf(p);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'สร้างไฟล์ PDF ไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }

  const trail = p.current?.unitTrail ?? [];

  return (
    <section className={`pfsec${embedded ? ' is-embedded' : ''}`}>
      <div className="wrap">
        {/* เส้นทางหน่วยงานซ้ำกับกล่อง "ข้อมูลอัตรา" ของแผงที่ครอบอยู่ จึงตัดออกเมื่อ hideIdentity */}
        {!hideIdentity && (
        <nav className="crumbs" aria-label="เส้นทางหน่วยงาน">
          {embedded ? (
            <span className="crumb-text">กระทรวงการคลัง</span>
          ) : (
            <button type="button" onClick={() => navigate({ name: 'org' })}>
              กระทรวงการคลัง
            </button>
          )}
          {trail.map((u) => (
            <span key={u.id} className="crumb">
              <span className="sep" aria-hidden="true">
                ›
              </span>
              {embedded ? (
                <span className="crumb-text">{u.name}</span>
              ) : (
                <button type="button" onClick={() => navigate({ name: 'unit', unitId: u.id })}>
                  {u.name}
                </button>
              )}
            </span>
          ))}
          <span className="crumb">
            <span className="sep" aria-hidden="true">
              ›
            </span>
            <b aria-current="page">{p.fullName}</b>
          </span>
        </nav>
        )}

        <div className="pf-print">
          <header className={`pfhero${hideIdentity ? ' is-bare' : ''}`}>
            {!hideIdentity && (
            <div className="pfid">
              <span className="pfav" aria-hidden="true">
                {initial(p.fullName)}
              </span>
              <div>
                <h2>{p.fullName}</h2>
                <p className="pfrole">
                  {p.current ? p.current.positionTitle : 'ไม่พบตำแหน่งที่ครองอยู่ในทะเบียนอัตรากำลัง'}
                </p>
                {/*
                  ลูกจ้างชั่วคราวและพนักงานราชการมีค่า level เท่ากับ category
                  ถ้าแสดงทั้งสองจะได้ป้ายซ้ำคำเดิมสองใบติดกัน ซึ่งดูเหมือนระบบพัง
                */}
                <div className="pfchips">
                  {p.current && <span className="pfchip is-level">{levelLabel(p.current.level)}</span>}
                  {p.current && p.current.category !== p.current.level && (
                    <span className="pfchip">{p.current.category}</span>
                  )}
                  {trail.length > 0 && (
                    <span className="pfchip">{trail[trail.length - 1]?.name}</span>
                  )}
                  {p.current?.posNo && (
                    <span className="pfchip">เลขที่ตำแหน่ง {p.current.posNo}</span>
                  )}
                </div>
              </div>
            </div>
            )}
            {!hideActions && (
            <div className="pfactions">
              <button
                type="button"
                className="pfbtn is-primary"
                onClick={() => void savePdf()}
                disabled={saving}
              >
                {saving ? 'กำลังสร้างไฟล์…' : 'บันทึกเป็น PDF'}
              </button>
              <button type="button" className="pfbtn" onClick={print}>
                พิมพ์
              </button>
            </div>
            )}
          </header>

          {pdfError && <p className="pferr">สร้างไฟล์ PDF ไม่สำเร็จ — {pdfError}</p>}

          {/* สี่ค่านี้ (อายุ · เกษียณ · อายุราชการ · ครองตำแหน่งปัจจุบัน) ซ้ำกับตาราง
              "ผู้ครองปัจจุบัน" ของแผงที่ครอบอยู่ทุกช่อง จึงตัดออกเมื่อ hideIdentity
              ⚠ ค่าที่ตารางนั้นไม่มีคือ "อายุราชการ" กับ "ครองตำแหน่งมาแล้ว" ซึ่งเป็นระยะเวลา
                ย้ายไปต่อท้ายวันที่ในตารางนั้นแทนแล้ว ไม่ได้หายไปเฉย ๆ */}
          {!hideIdentity && (
          <dl className="pffacts">
            <Fact
              label="อายุ"
              value={p.age != null ? `${p.age} ปี` : '—'}
              note={`เกิด ${thaiDate(p.birthDate)}`}
            />
            <Fact
              label="เกษียณอายุราชการ"
              value={thaiDate(p.retireDate)}
              note={
                p.retireFiscalYear
                  ? `อัตราว่าง 1 ต.ค. ${p.retireFiscalYear} · ${untilText(p.retireDate)}`
                  : undefined
              }
              tone={retireTone(p.retireDate)}
            />
            <Fact
              label="อายุราชการ"
              value={sinceText(p.current?.entryDate ?? null)}
              note={`บรรจุ ${thaiDate(p.current?.entryDate ?? null)}`}
            />
            <Fact
              label="ครองตำแหน่งปัจจุบัน"
              value={sinceText(p.current?.occupyDate ?? null)}
              note={`ตั้งแต่ ${thaiDate(p.current?.occupyDate ?? null)}`}
            />
          </dl>
          )}

          <div className={`pfgrid${hideCareer ? ' is-solo' : ''}`}>
            {!hideCareer && (
            <main className="pfmain">
              {/*
                ⚠ เดิมตรงนี้มีป้าย "ข้อมูลจริงจาก DPIS" — เอาออกตามที่ผู้ใช้ขอ

                และเอาออกถูกแล้ว เพราะป้ายนั้นเขียนตายไว้ในหน้าจอ ไม่ได้ถามเซิร์ฟเวอร์ว่า
                ไฟล์ที่กำลังอ่านอยู่เป็นชุดจริงหรือชุดสาธิต ตอนที่ระบบรันด้วย
                dpis-dump-demo.json (ซึ่งเป็นค่าเริ่มต้นของ npm run dev) ป้ายนี้จะยัง
                ขึ้นว่า "ข้อมูลจริง" ทั้งที่ประวัติทั้งก้อนเป็นของสังเคราะห์ — ป้ายที่โกหก
                อันตรายกว่าไม่มีป้าย

                ที่บอกความจริงคือแถบ synthetic ในคอลัมน์ขวา ซึ่งตัดสินจากค่าที่เซิร์ฟเวอร์
                ส่งมา ไม่ใช่จากเงื่อนไขที่เขียนไว้ในหน้าจอ — ห้ามเอาป้ายแบบเดิมกลับมา
              */}
              <div className="pfsec-h">
                <h3>ประวัติการดำรงตำแหน่ง</h3>
              </div>
              <p className="pfnote">
                {p.careerHistory.items.length.toLocaleString('th-TH')} รายการ ·{' '}
                {careerSpan(p.careerHistory.items)}
              </p>
              <Timeline periods={p.careerHistory.items} />
            </main>
            )}

            <aside className="pfaside">
              <div className="pfsec-h">
                <h3>ข้อมูลประกอบ</h3>
              </div>
              {/*
                ⚠ เดิมตรงนี้มีแถบกำกับว่าข้อมูลในคอลัมน์นี้เป็นของสมมุติ — ผู้ใช้สั่งเอาออก
                  เพราะผู้ใช้ของระบบนี้รู้อยู่แล้วว่ากำลังดูชุดสาธิต

                  ฟิลด์ origin ของแต่ละส่วน ('จริง' | 'สมมุติ') ยังส่งมาจากเซิร์ฟเวอร์ครบ
                  ถ้าจะเอาแถบกลับมา เช็ก origin ของทั้งห้าส่วนแล้วใส่ <p className="pfwarn"> คืนตรงนี้
              */}
              <Panel
                title="การศึกษา"
                count={p.education.items.length}
                summary={p.education.items[0]?.degree ?? 'ไม่มีข้อมูล'}
              >
                <ul className="plist">
                  {p.education.items.map((e, i) => (
                    <li key={i}>
                      <b>{[e.degree, e.field].filter(Boolean).join(' ') || '—'}</b>
                      <span className="pmeta">
                        {[e.institution, e.graduationYear && `จบปี ${e.graduationYear}`]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel
                title="การฝึกอบรม"
                count={p.training.items.length}
                summary={`รวม ${p.training.items.reduce((s, t) => s + (t.hours ?? 0), 0).toLocaleString('th-TH')} ชั่วโมง`}
              >
                <ul className="plist">
                  {p.training.items.map((t, i) => (
                    <li key={i}>
                      <b>{t.course ?? '—'}</b>
                      <span className="pmeta">
                        {[
                          t.provider,
                          t.startDate && thaiDate(t.startDate),
                          t.hours && `${t.hours} ชั่วโมง`,
                          t.certificate && 'มีวุฒิบัตร',
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel
                title="ผลการประเมิน"
                count={p.performance.items.length}
                summary={
                  p.performance.items.length > 0
                    ? `ล่าสุด ${p.performance.items[p.performance.items.length - 1]?.rating ?? '—'}`
                    : 'ไม่มีข้อมูล'
                }
              >
                <ul className="pbars">
                  {[...p.performance.items].reverse().map((r, i) => (
                    <li key={i}>
                      <span className="pb-k tnum">{r.year ?? '—'}</span>
                      <span className="pb-track">
                        <span
                          className="pb-fill"
                          style={{ width: `${Math.max(0, Math.min(100, r.score ?? 0))}%` }}
                        />
                      </span>
                      <span className="pb-v">
                        {r.rating ?? '—'}
                        {r.score != null && <em className="tnum"> {r.score}</em>}
                      </span>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel
                title="สมรรถนะ"
                count={Object.keys(p.competencies.items).length}
                summary="เต็ม 5 คะแนน"
              >
                <ul className="pbars">
                  {Object.entries(p.competencies.items).map(([k, v]) => (
                    <li key={k}>
                      <span className="pb-k">{COMPETENCY_TH[k] ?? k}</span>
                      <span className="pb-track">
                        <span className="pb-fill" style={{ width: `${(v / 5) * 100}%` }} />
                      </span>
                      <span className="pb-v tnum">{v.toFixed(1)}</span>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel
                title="ภาษา"
                count={p.languages.items.length}
                summary={p.languages.items.map((l) => l.language).filter(Boolean).join(', ') || 'ไม่มีข้อมูล'}
              >
                <ul className="plist">
                  {p.languages.items.map((l, i) => (
                    <li key={i}>
                      <b>{l.language ?? '—'}</b>
                      <span className="pmeta">
                        ฟัง {l.listening ?? '—'} · พูด {l.speaking ?? '—'} · อ่าน {l.reading ?? '—'}{' '}
                        · เขียน {l.writing ?? '—'}
                      </span>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel
                title="ผลงาน"
                count={p.achievements.items.length}
                summary={p.achievements.items[0]?.title ?? 'ไม่มีข้อมูล'}
              >
                <ul className="plist">
                  {p.achievements.items.map((a, i) => (
                    <li key={i}>
                      <b>{a.title ?? '—'}</b>
                      <span className="pmeta">
                        {[a.year, a.role, a.level, a.impact].filter(Boolean).join(' · ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel
                title="รางวัล"
                count={p.awards.items.length}
                summary={p.awards.items[0]?.title ?? 'ไม่มีข้อมูล'}
              >
                <ul className="plist">
                  {p.awards.items.map((a, i) => (
                    <li key={i}>
                      <b>{a.title ?? '—'}</b>
                      <span className="pmeta">{[a.year, a.level].filter(Boolean).join(' · ')}</span>
                    </li>
                  ))}
                </ul>
              </Panel>

              {/* เครื่องราชอิสริยาภรณ์ — แสดงเฉพาะ "ถืออะไรอยู่"
                  ⚠ ห้ามเติมบรรทัดประเภท "ถึงคิวขอชั้นถัดไปแล้ว" ลงในแผงนี้
                     เกณฑ์การขอพระราชทานอยู่ในระเบียบสำนักนายกรัฐมนตรีฯ ซึ่งยังไม่ได้ถอดเข้าระบบ
                     (ดู ProfileDecoration ใน domain/profile.ts) */}
              <Panel
                title="เครื่องราชอิสริยาภรณ์"
                count={p.decorations.items.length}
                summary={
                  p.decorations.items.length === 0
                    ? 'ไม่มีข้อมูล'
                    : `ชั้นสูงสุด ${p.decorations.items[p.decorations.items.length - 1]?.name ?? '—'}`
                }
              >
                {p.decorations.items.length === 0 ? (
                  /* อาเรย์ว่างมีสองความหมาย ต้องไม่เขียนว่า "ไม่เคยได้รับ" ลอย ๆ
                     เพราะพนักงานราชการและลูกจ้างชั่วคราวไม่อยู่ในข่ายตั้งแต่ต้น */
                  <p className="pempty">
                    ไม่มีรายการในไฟล์ข้อมูล — อาจเป็นเพราะยังไม่เคยได้รับ
                    หรือประเภทบุคลากรนี้ไม่อยู่ในข่ายขอพระราชทาน
                  </p>
                ) : (
                  <ul className="plist">
                    {p.decorations.items.map((d, i) => (
                      <li key={i}>
                        <b>
                          {d.shortName && <span className="deco">{d.shortName}</span>}
                          {d.name ?? '—'}
                        </b>
                        <span className="pmeta">{d.year ? `ได้รับพระราชทาน พ.ศ. ${d.year}` : '—'}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>

              <Panel
                title="ประวัติทางวินัย"
                count={p.disciplinary.items.records.length}
                summary={
                  p.disciplinary.items.hasAction ? 'มีเรื่องที่บันทึกไว้' : 'ไม่มีเรื่องที่บันทึกไว้'
                }
                tone={p.disciplinary.items.hasAction ? 'alert' : undefined}
              >
                {p.disciplinary.items.records.length === 0 ? (
                  <p className="pmeta">ไม่มีรายการ</p>
                ) : (
                  <ul className="plist">
                    {p.disciplinary.items.records.map((d, i) => (
                      <li key={i}>
                        <b>{d.type ?? '—'}</b>
                        <span className="pmeta">
                          {[d.year, d.status].filter(Boolean).join(' · ')}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* ไทม์ไลน์ประวัติการดำรงตำแหน่ง                                        */
/* ------------------------------------------------------------------ */

/**
 * เรียงจากใหม่ไปเก่า เพราะคำถามที่คนเปิดหน้านี้ถามบ่อยที่สุดคือ
 * "ตอนนี้อยู่ตำแหน่งอะไร มานานเท่าไหร่แล้ว" ไม่ใช่ "เริ่มรับราชการที่ไหน"
 *
 * แถบความยาวเทียบกับช่วงที่ยาวที่สุดของคนคนนั้น ไม่ใช่ค่าคงที่ทั้งระบบ
 * เพราะจุดประสงค์คือให้กวาดตาเห็นว่าช่วงไหนอยู่นาน ช่วงไหนผ่านไปเร็ว ภายในคนคนเดียว
 */
function Timeline({ periods }: { periods: ServicePeriod[] }) {
  if (periods.length === 0) {
    return <p className="pmeta">ไม่มีประวัติการดำรงตำแหน่งในข้อมูลชุดนี้</p>;
  }

  const longest = Math.max(...periods.map((h) => h.months), 1);
  const rows = [...periods].reverse();

  return (
    <ol className="ptl">
      {rows.map((h, i) => (
        <li key={`${h.start}-${i}`} className={h.isCurrent ? 'is-now' : ''}>
          <span className="ptl-dot" aria-hidden="true" />
          <div className="ptl-body">
            <div className="ptl-top">
              <b>{h.title}</b>
              {h.isCurrent && <span className="ptl-tag is-now">ปัจจุบัน</span>}
              {h.acting && <span className="ptl-tag">รักษาการ</span>}
            </div>
            <div className="ptl-meta">
              <span className="ptl-lv">{periodLevel(h)}</span>
              <span>
                {thaiDate(h.start)} – {h.end ? thaiDate(h.end) : 'ปัจจุบัน'}
              </span>
              <span className="ptl-dur">{monthsTH(h.months)}</span>
            </div>
            <span className="ptl-bar" aria-hidden="true">
              <span style={{ width: `${(h.months / longest) * 100}%` }} />
            </span>
            {h.orderNo && <span className="ptl-order">คำสั่งที่ {h.orderNo}</span>}
          </div>
        </li>
      ))}
    </ol>
  );
}

/* ------------------------------------------------------------------ */
/* ชิ้นส่วนย่อย                                                        */
/* ------------------------------------------------------------------ */

function Fact({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'alert';
}) {
  return (
    <div className={`pffact${tone ? ` is-${tone}` : ''}`}>
      <dt>{label}</dt>
      <dd>{value}</dd>
      {note && <p>{note}</p>}
    </div>
  );
}

/**
 * แผงหนึ่งเรื่องในคอลัมน์ข้าง
 *
 * เรื่องที่ไม่มีข้อมูลเลย (count เป็นศูนย์) ปุ่มจะกดไม่ได้และไม่มีเครื่องหมายกาง
 * ตั้งใจให้ยังแสดงอยู่ ไม่ซ่อนทิ้ง เพราะ "ไม่มีรางวัล" กับ "ระบบไม่มีช่องรางวัล"
 * เป็นคนละเรื่องกันสำหรับคนที่กำลังตรวจประวัติ
 */
function Panel({
  title,
  count,
  summary,
  tone,
  children,
}: {
  title: string;
  count: number;
  summary: string;
  tone?: 'alert';
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const empty = count === 0;

  return (
    <div className={`pfpanel${open ? ' is-open' : ''}${tone ? ` is-${tone}` : ''}`}>
      <button
        type="button"
        className="pfpanel-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        disabled={empty}
      >
        <span className="pfpanel-t">{title}</span>
        <span className="pfpanel-s">{summary}</span>
        <span className={`pfpanel-n tnum${empty ? ' is-zero' : ''}`}>{count}</span>
        <span className="pfpanel-c" aria-hidden="true">
          {empty ? '' : open ? '−' : '+'}
        </span>
      </button>
      {open && !empty && <div className="pfpanel-b">{children}</div>}
    </div>
  );
}

const COMPETENCY_TH: Record<string, string> = {
  leadership: 'ภาวะผู้นำ',
  strategicThinking: 'การคิดเชิงยุทธศาสตร์',
  changeManagement: 'การบริหารการเปลี่ยนแปลง',
  communication: 'การสื่อสาร',
  digitalLiteracy: 'ทักษะดิจิทัล',
  teamwork: 'การทำงานเป็นทีม',
};

/**
 * เติมคำว่า "ระดับ" เฉพาะกับระดับของข้าราชการพลเรือนสามัญ
 *
 * เดิมเติมหน้าทุกค่า ทำให้ได้ "ระดับลูกจ้างชั่วคราว" และ "ระดับกลุ่มงานบริการ"
 * ซึ่งไม่ใช่คำที่ใช้กันในระบบราชการ และอ่านแล้วสะดุดทันทีสำหรับคนที่ทำงาน HR
 */
export function levelLabel(level: string): string {
  const v = level.trim();
  if (!v) return '—';
  if (v.startsWith('ลูกจ้าง') || v.startsWith('กลุ่มงาน') || v.startsWith('ระดับ')) return v;
  return `ระดับ${v}`;
}

function periodLevel(h: ServicePeriod): string {
  const raw = h.level ?? h.legacyLevel ?? h.nonCivilServiceLevel;
  if (!raw) return 'ระบุระดับไม่ได้';
  return h.level ? levelLabel(h.level) : raw;
}

function careerSpan(periods: ServicePeriod[]): string {
  if (periods.length === 0) return 'ไม่มีข้อมูล';
  const first = periods[0];
  const total = periods.reduce((s, h) => s + h.months, 0);
  return `ตั้งแต่ ${thaiDate(first?.start ?? null)} รวม ${monthsTH(total)}`;
}

function initial(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts.length > 1 ? parts[1] : parts[0];
  return (first ?? '?').slice(0, 1);
}

/** ใกล้เกษียณใน 1 ปี ให้ระบายเป็นสิ่งที่ต้องวางแผน ไม่ใช่ข้อเท็จจริงเฉย ๆ */
function retireTone(iso: string | null): 'alert' | undefined {
  const months = monthsBetween(new Date(), iso);
  return months !== null && months >= 0 && months <= 12 ? 'alert' : undefined;
}

function thaiDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' });
}

function monthsTH(months: number): string {
  if (months <= 0) return 'ไม่ถึงหนึ่งเดือน';
  const y = Math.floor(months / 12);
  const m = months % 12;
  return [y > 0 ? `${y} ปี` : '', m > 0 ? `${m} เดือน` : ''].filter(Boolean).join(' ');
}

/** นับเดือนจากวันที่กำหนดถึงวันนี้ */
function sinceText(iso: string | null): string {
  const months = monthsBetween(iso, new Date());
  return months === null ? '—' : monthsTH(months);
}

/** เหลืออีกเท่าไหร่ถึงวันที่กำหนด */
function untilText(iso: string | null): string {
  const months = monthsBetween(new Date(), iso);
  if (months === null) return '—';
  if (months < 0) return 'ครบกำหนดแล้ว';
  return `อีก ${monthsTH(months)}`;
}

function monthsBetween(from: string | Date | null, to: string | Date | null): number | null {
  if (!from || !to) return null;
  const a = from instanceof Date ? from : new Date(from);
  const b = to instanceof Date ? to : new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  let months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (b.getDate() < a.getDate()) months -= 1;
  return months;
}
